/* Presentation-only integration: Shopify Forms retains validation and submission. */
(() => {
  if (customElements.get('ic-b2b-form')) return;

  const definitions = new Map();
  const getDefinitions = (shop) => {
    if (!/^[a-z0-9-]+\.myshopify\.com$/i.test(shop)) return Promise.resolve([]);
    if (!definitions.has(shop)) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      definitions.set(shop, fetch(
        `https://forms.shopifyapps.com/api/v2/form_instances?shopify_domain=${encodeURIComponent(shop)}`,
        { credentials: 'omit', signal: controller.signal }
      ).then((response) => (response.ok ? response.json() : []))
        .then((data) => (Array.isArray(data) ? data : []))
        .catch(() => [])
        .finally(() => clearTimeout(timeout)));
    }
    return definitions.get(shop);
  };

  class B2BForm extends HTMLElement {
    connectedCallback() {
      this.attempts = 0;
      this.containerObserver = new MutationObserver(() => this.findRoot());
      this.containerObserver.observe(this, { childList: true, subtree: true });
      this.rootObserver = new MutationObserver(() => this.apply());
      this.findRoot();
      customElements.whenDefined('shopify-forms-embed').then(() => {
        if (this.isConnected) this.findRoot();
      });

      if (this.dataset.mode === 'registration') {
        getDefinitions(this.dataset.shop).then((forms) => {
          this.fields = forms.find((form) => String(form.id) === this.dataset.formId)
            ?.form_definition?.form?.fields || [];
          if (this.isConnected) this.apply();
        });
      }
    }

    disconnectedCallback() {
      clearTimeout(this.retry);
      this.containerObserver?.disconnect();
      this.rootObserver?.disconnect();
      this.root = null;
    }

    findRoot() {
      clearTimeout(this.retry);
      if (!/^\d+$/.test(this.dataset.formId || '')) return;
      const host = this.querySelector(
        `[data-forms-id="forms-root-${this.dataset.formId}"] shopify-forms-embed`
      );
      if (host?.shadowRoot) {
        if (this.root !== host.shadowRoot) {
          this.rootObserver.disconnect();
          this.root = host.shadowRoot;
          this.apply();
        }
      } else if (this.attempts++ < 120) {
        // Shadow attachment itself isn't a light-DOM mutation. Stop polling after 30s.
        this.retry = setTimeout(() => this.findRoot(), 250);
      }
    }

    setText(element, text) {
      if (!element || !text || element.textContent === text) return;
      // Preserve React-owned nodes and the label's for/id associations.
      if (element.childNodes.length === 1 && element.firstChild.nodeType === Node.TEXT_NODE) {
        element.firstChild.nodeValue = text;
      }
    }

    markRequired(name) {
      const input = this.root.getElementById(name);
      if (!input) {
        // Country selectors are buttons, not native inputs in Shopify Forms.
        const selectedValue = this.root.getElementById(`${name}-selector-value`);
        if (selectedValue) selectedValue.dataset.icRequired = '';
        return;
      }
      const label = this.root.getElementById(`label-${name}`)
        || this.root.querySelector(`label[for="${CSS.escape(name)}"]`);
      if (label) label.dataset.icRequired = '';
      if (input.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select, [role="combobox"]')) {
        input.setAttribute('aria-required', 'true');
      }
    }

    apply() {
      if (!this.root || !this.isConnected) return;
      this.rootObserver.disconnect();
      try {
        const form = this.root.querySelector('form');
        const heading = this.root.querySelector('h2');
        const header = heading?.closest('section');
        if (!form) {
          this.root.querySelector('[data-ic-required-note]')?.remove();
          return; // Never replace the app's success or error confirmation.
        }

        if (this.dataset.mode === 'newsletter') {
          this.setText(heading, this.dataset.heading);
          this.setText(header?.querySelector('p'), this.dataset.description);
          return;
        }

        this.setText(this.root.getElementById('label-billing_address_recipient'), this.dataset.companyLabel);
        ['billing_address', 'shipping_address'].forEach((address) => {
          this.setText(this.root.getElementById(`label-${address}_last_name`), this.dataset.surnameLabel);
        });

        if (!this.root.querySelector('[data-ic-required-style]')) {
          const style = document.createElement('style');
          style.dataset.icRequiredStyle = '';
          style.textContent = '[data-ic-required]::after{content:" *"}'
            + '[data-ic-required-note]{font:var(--forms-body-font);color:var(--body-text-color);margin:8px 0 0}';
          this.root.append(style);
        }

        let hasRequired = false;
        (this.fields || []).forEach((field) => {
          if (!field.validators?.some((validator) => validator.name === 'required')) return;
          hasRequired = true;
          if (field.type === 'address') {
            // Forms' address validator requires these three fields, not every subfield.
            // Verified against Shopify Forms parseField (forms-2604); keep optional fields optional.
            ['country', 'address1', 'city'].forEach((part) => this.markRequired(`${field.name}_${part}`));
          } else if (field.type === 'radio') {
            const input = Array.from(form.querySelectorAll('input[type="radio"]'))
              .find((radio) => radio.name === field.name);
            const legend = input?.closest('fieldset')?.querySelector('legend');
            if (legend) legend.dataset.icRequired = '';
          } else {
            this.markRequired(field.name);
          }
        });
        if (hasRequired && header && !header.querySelector('[data-ic-required-note]')) {
          const note = document.createElement('p');
          note.dataset.icRequiredNote = '';
          note.textContent = this.dataset.requiredNote;
          header.append(note);
        }
      } finally {
        this.rootObserver.observe(this.root, { childList: true, subtree: true, characterData: true });
      }
    }
  }

  customElements.define('ic-b2b-form', B2BForm);
})();
